import { useEffect } from "react"
import Screen from "./components/Screen"
import { spacing } from "./styles"

import "./Products.scss"
import Button from "./components/Button"
import { useLocation, useNavigate } from "react-router-dom"
import Flex from "../../components/Flex"
import useNav from "../../components/useNav"
import Icons from "../../components/Icons"
import datasource from "../../datasource"

interface ProductCta {
    label: string
    type: "contact" | "link" | "page"
    link: string
}

interface Product {
    slug: string
    title: string
    tagline: string
    description: string
    capabilities: string[]
    icon: React.JSX.Element
    cta: ProductCta
}

const {
    pageTitle,
    pageSubTitle,
    items,
    welcomeTitle,
    welcomeSubTitle
} = datasource.products

export default () => {

    const navigate = useNavigate()
    const location = useLocation()

    useEffect(() => {
        if (location.hash) {
            const slug = location.hash.replace("#", "")
            setTimeout(() => {
                document.getElementById(`product-${slug}`)?.scrollIntoView({ behavior: "smooth", block: "start" })
            }, 100)
        }
    }, [location.hash])

    const goToCareer = () => {
        navigate("/careers")
        document.scrollingElement?.scrollTo({ top: 0, behavior: "smooth" })
    }

    return (
        <div className="products">
            <Screen className="products__title-screen">
                <Flex vertical align="center" gap={spacing("4xl")}>
                    <div className="products__title">{pageTitle}</div>
                    <div className="products__subTitle">{pageSubTitle}</div>
                </Flex>
            </Screen>
            <Screen className="products__grid-screen">
                <div className="products__grid-box">
                    <div className="products__grid">
                        {(items as Product[]).map(it => <ProductCard key={it.slug} product={it}></ProductCard>)}
                    </div>
                </div>
            </Screen>
            <div className="products__welcome-box">
                <Flex className="products__welcome" justify="space-between">
                    <Flex vertical gap={10}>
                        <div className="products__welcome__title">{welcomeTitle}</div>
                        <div className="products__welcome__subTitle">{welcomeSubTitle}</div>
                    </Flex>
                    <Flex vertical justify="flex-end">
                        <Button onClick={() => goToCareer()}>New Openings</Button>
                    </Flex>
                </Flex>
            </div>
        </div>
    )
}

const ProductCard = (props: { product: Product }) => {
    const navigate = useNavigate()
    const { nav } = useNav()

    const doCta = () => {
        const { type, link } = props.product.cta
        if (type === "contact") {
            // deliberately not scrolling to top: land on home, then on the contact form
            navigate(link)
            setTimeout(() => {
                document.scrollingElement?.scrollTo({ top: window.innerHeight, behavior: "smooth" })
            }, 100)
        } else if (type === "page") {
            // useNav navigates and smooth-scrolls to top, same as the inline form
            nav(link)
        } else {
            window.open(link, "_blank", "noopener,noreferrer")
        }
    }

    return (
        <Flex vertical id={`product-${props.product.slug}`} className="product-card" gap={spacing("xl")}>
            <Flex align="center" justify="center" className="product-card__icon-box">
                {props.product.icon}
            </Flex>
            <Flex vertical gap={spacing("xs")}>
                <div className="product-card__title">{props.product.title}</div>
                <div className="product-card__tagline">{props.product.tagline}</div>
            </Flex>
            <div className="product-card__description">{props.product.description}</div>
            <Flex vertical gap={spacing("md")} className="product-card__capabilities">
                {props.product.capabilities.map(capability => (
                    <div key={capability} className="product-card__capability">{capability}</div>
                ))}
            </Flex>
            <Flex className="product-card__cta">
                <Button onClick={doCta}>
                    <Flex gap={spacing("md")} reposive={false} align="center">
                        {props.product.cta.label}
                        <Icons.ArrowUp width={16} height={16}></Icons.ArrowUp>
                    </Flex>
                </Button>
            </Flex>
        </Flex>
    )
}
